import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import closeicon from "../../../assets/images/close.svg";
import { getLoggedInUserDataFromLocalStorage } from "../../../config/config";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import { setLoginTokenInfo } from "../../../redux/features/loginRegisterSlice";
import Button from "../../common/Button/Button";
import BackdropModal from "../../common/modal/backdropModal";

const PersonalCyberLoginPromptModal = ({
  show,
  onClose,
  onLoginSuccess,
  onSkip,
}) => {
  const translate = useLanguageTranslate();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Benefits data array
  const benefitsData = [
    {
      en: "View your personal cyber insurance policy anytime directly from your account.",
      ar: "اعرض وثيقة تأمينك في أي وقت مباشرةً من حسابك.",
    },
    {
      en: "Faster claims processing with pre-filled data when you need it most.",
      ar: "معالجة المطالبات بسرعة أكبر باستخدام البيانات المعبأة مسبقًا عند الضرورة.",
    },
    {
      en: "Personalized service with all your insurance details securely stored in one place.",
      ar: "خدمة مخصصة مع تخزين جميع تفاصيل التأمين الخاصة بك بشكل آمن في مكان واحد.",
    },
  ];

  if (!show) return null;

  const handleLoginOrRegister = () => {
    // Open sign-in page in a new window/tab
    // This preserves the current personal cyber policy state in the original window
    const signInUrl = "/sign-in";
    const newWindow = window.open(
      signInUrl,
      "_blank",
      "width=1200,height=800,scrollbars=yes,resizable=yes"
    );

    // Focus the new window
    if (newWindow) {
      newWindow.focus();
    }

    // Set up a listener for when the user returns from login
    const handleStorageChange = (e) => {
      if (e.key === "loginSuccess" && e.newValue === "true") {
        // User successfully logged in, get the token info from localStorage and update Redux
        const tokenInfo = getLoggedInUserDataFromLocalStorage();
        console.log("Login success detected, token info:", tokenInfo);
        if (tokenInfo) {
          dispatch(setLoginTokenInfo(tokenInfo));
          console.log("Redux state updated with token info");
        }

        // Proceed to next step
        if (onLoginSuccess) {
          onLoginSuccess();
        }
        // Clean up the flag
        localStorage.removeItem("loginSuccess");
        window.removeEventListener("storage", handleStorageChange);
      }
    };

    // Listen for login success from the new window
    window.addEventListener("storage", handleStorageChange);

    // Optional: Clean up listener if window is closed without login
    const checkWindowClosed = setInterval(() => {
      if (newWindow && newWindow.closed) {
        window.removeEventListener("storage", handleStorageChange);
        clearInterval(checkWindowClosed);
      }
    }, 1000);

    // DON'T close the modal here - let the login detection mechanisms handle it
    // The modal should stay open until login is detected or user manually closes it
  };

  return (
    <BackdropModal show={show} onClose={onClose} hideClosebtn>
      <>
        <div className="flex justify-between items-center rtl:items-start mb-4 -mt-6">
          <h2 className="text-[#1A2024] text-2xl max767:text-xl not-italic font-semibold leading-6 rtl:max-w-[480px] rtl:leading-[32px]">
            {translate(
              "Secure Your Personal Cyber Policy with Ease",
              "قم بحفظ وثيقة التأمين السيبراني الشخصي بكل سهولة"
            )}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rtl:bg-gray-100 rounded-full max767:bg-[#E5E9EB]"
          >
            <img src={closeicon} alt="Close" className="w-6 h-6" />
          </button>
        </div>
        <div className="w-full max-h-[80vh] max-w-[540px]">
          {/* Content */}
          <div className="mb-6">
            <p className="text-[#1A2024] text-base not-italic font-normal leading-6 tracking-[-0.16px] mb-4">
              {translate(
                "Complete your personal cyber insurance policy under your name and enjoy faster claims and seamless management.",
                "أكمل وثيقة التأمين السيبراني الشخصية باسمك واستمتع بمطالبات أسرع وإدارة سلسة لوثائقك."
              )}
            </p>

            <div className="bg-[#F6F8F9] rounded-lg p-4 mb-4">
              <h3 className="text-[#1A2024] text-lg font-medium mb-2">
                {translate(
                  "Why Login or Register?",
                  "لماذا عليك تسجيل الدخول أو إنشاء حساب؟"
                )}
              </h3>
              <div className="text-[#000] text-sm space-y-1">
                {benefitsData.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      className="mt-0.5 flex-shrink-0"
                    >
                      <path
                        d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                        fill="#47D16C"
                      />
                    </svg>
                    <span>{translate(benefit.en, benefit.ar)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-row gap-3 max767:flex-col-reverse">
            <Button
              label={translate("Skip", "تخطي")}
              onSubmit={onSkip}
              className="w-full bg-white border border-[#DDE2E4] text-[#48535B] rounded-[10px] text-center text-sm font-semibold py-2 max767:w-full"
              fontSize="text-sm"
            />
            <Button
              label={translate(
                "Login or Register",
                "تسجيل الدخول أو إنشاء حساب"
              )}
              onSubmit={handleLoginOrRegister}
              className="w-full rounded-[10px] text-center text-sm font-semibol py-2 max767:w-full"
              fontSize="text-sm"
            />
          </div>
        </div>
      </>
    </BackdropModal>
  );
};

export default PersonalCyberLoginPromptModal;
